﻿/**
 * Varno branje parametra `next` po prijavi (T1224).
 *
 * Javna tržnica pošlje popisovalca na `/login?next=/marketplace?job=5`, da po
 * prijavi pristane pri **tistem** povpraševanju. Parameter pride iz naslova,
 * torej ga lahko sestavi kdorkoli — brez preverjanja bi bila to odprta
 * preusmeritev: `/login?next=https://zlonamerno.si` bi po uspešni prijavi
 * odnesla uporabnika na tuje mesto, ki bi bilo videti kot del aplikacije.
 *
 * Zato **dovoljena je izključno pot znotraj te aplikacije**: začeti mora z eno
 * poševnico in ne sme biti zavajajoča oblika, ki jo brskalnik razume kot
 * absoluten naslov.
 */

/**
 * Kam pristane uporabnik, kadar `next` ni podan ali ni varen.
 *
 * T2529: edini privzeti cilj po prijavi. Prej so bili trije — prijavni obrazec
 * je peljal na `/`, že prijavljenega je `LoginPage` pošiljal na `/projects`,
 * pot `/login` pa na `/dashboard` — in ker po prijavi tečeta obrazec in
 * preusmeritev poti hkrati (T2519), je bil cilj odvisen od vrstnega reda.
 */
export const DEFAULT_AFTER_LOGIN = '/dashboard';

// Nadzorne znake lovimo namenoma: `next` z vrinjenim CR/LF omogoča razbitje
// glave odgovora, zato je prav njihova prisotnost tisto, kar zavračamo.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

// T3000: prijavna stran kot cilj prijave bi uporabnika vrnila nase. Router
// velikih črk ne loči, zato tudi `/LOGIN`; `/login-help` ostane dovoljen.
const LOGIN_PATH = /^\/login(?:[/?#]|$)/i;

/**
 * Vrne pot, na katero se sme preusmeriti, ali `DEFAULT_AFTER_LOGIN`.
 *
 * Zavrnjene oblike in zakaj:
 *  - `https://tuje.si`, `http://…`, `javascript:…` — absoluten oz. shemni naslov;
 *  - `//tuje.si` — protokolno relativen naslov, ki ga brskalnik razume kot
 *    absolutnega, čeprav se začne s poševnico;
 *  - `/\tuje.si` — nekateri brskalniki obrnjeno poševnico obravnavajo enako kot
 *    navadno, zato je to isti obhod v drugi preobleki;
 *  - `marketplace` brez vodilne poševnice — razrešilo bi se relativno glede na
 *    trenutno pot in cilj ne bi bil določen;
 *  - `/login` (tudi s poizvedbo ali hashem, T3000) — prijava bi vodila nase;
 *  - prazen niz in `null`.
 *
 * Nadzorni znaki se **zavrnejo in ne obrežejo**: `\n` sredi naslova je znak
 * poskusa, ne tipkarske napake.
 */
export function safeNextPath(next: string | null | undefined): string {
  if (!next) return DEFAULT_AFTER_LOGIN;
  if (CONTROL_CHARS.test(next)) return DEFAULT_AFTER_LOGIN;
  if (!next.startsWith('/')) return DEFAULT_AFTER_LOGIN;
  // Drugi znak odloča: `//` in `/\` sta obe poti do tujega gostitelja.
  if (next.startsWith('//') || next.startsWith('/\\')) return DEFAULT_AFTER_LOGIN;
  if (LOGIN_PATH.test(next)) return DEFAULT_AFTER_LOGIN;
  return next;
}

/**
 * T2376 — pot do prijave, ki uporabnika po prijavi vrne na `path`.
 *
 * Uporablja se ob preklicu ponovne prijave (seja potekla med delom): odjava
 * nato vodi na `/login?next=<trenutna pot>`. Pot gre skozi isto merilo kot
 * branje (`safeNextPath`) — kar ne bi preživelo branja, se niti ne zapiše.
 */
export function loginPathWithNext(path: string | null | undefined): string {
  const safe = safeNextPath(path);
  if (safe !== path || safe === DEFAULT_AFTER_LOGIN || safe.startsWith('/login')) return '/login';
  return `/login?next=${encodeURIComponent(safe)}`;
}
